import { CloseIcon } from "@/components/features/SquadBuilder/RightPanel/PanelHeader/PanelHeader";
import { arrow } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import { Helmet } from "react-helmet";
import { useMediaQuery } from "usehooks-ts";

interface HeroSectionProps {
  title: string;
  children?: React.ReactNode;
  content?: string;
  canonicalLink?: string;
  titleBackgroundColor?: string;
  navigateBack?: () => void;
  onClose?: () => void;
  shouldUseMobileTitle?: boolean;
  mobileChildren?: React.ReactNode;
  titleIcon?: React.ReactNode;
}

export const DesktopHeroSectionTitle = ({
  titleBackgroundColor,
  title,
  navigateBack,
  onClose,
  titleIcon,
}: {
  titleBackgroundColor: string;
  title: string;
  navigateBack?: () => void;
  onClose?: () => void;
  titleIcon?: React.ReactNode;
}) => (
  <div
    className={cn(
      "h-8 absolute top-0 left-0 px-8 items-center right-0 text-left justify-center hidden touch-device:flex z-10 text-white mx-auto",
      titleBackgroundColor,
    )}
  >
    <div className="flex flex-row gap-x-2 max-w-content mx-auto w-full">
      {navigateBack && (
        <button
          className="flex flex-row gap-x-2 whitespace-nowrap items-center my-auto p-2"
          onClick={navigateBack}
        >
          <div className="rotate-90 w-3 h-3">{arrow}</div>
        </button>
      )}
      <div className="flex flex-row items-center gap-x-2 mr-auto">
        <h1 className="font-semibold mt-0.5">{title}</h1>
        {titleIcon}
      </div>

      {onClose && (
        <button onClick={onClose} className="">
          <CloseIcon />
        </button>
      )}
    </div>
  </div>
);

export const MobileHeroSectionTitle = ({
  title,
  navigateBack,
  onClose,
  children,
  titleIcon,
}: {
  title: string;
  navigateBack?: () => void;
  onClose?: () => void;
  children?: React.ReactNode;
  titleIcon?: React.ReactNode;
}) => {
  return (
    <>
      <div
        className={cn(
          "h-12 w-full flex flex-col bg-panel-base fixed left-0 right-0 z-10 top-0",
        )}
      >
        {children ? (
          children
        ) : (
          <div className="flex flex-row h-full items-center justify-start gap-x-2 max-w-content mx-auto w-full">
            {navigateBack && (
              <button
                className="flex flex-row gap-x-2 whitespace-nowrap items-center z-10 p-4"
                onClick={navigateBack}
              >
                <span className="rotate-90 w-3 h-3">{arrow}</span>
              </button>
            )}

            <div className="mx-auto absolute left-0 right-0 w-full flex flex-row items-center justify-center max-w-[80%] gap-x-1">
              <h1 className="truncate">{title}</h1>
              {titleIcon}
            </div>

            {onClose && (
              <button onClick={onClose} className="absolute right-0 p-4 ">
                <CloseIcon />
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export const DesktopHeroSectionContent = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <div className="absolute top-10 left-0 pl-8 sb-xl:pl-0 items-center right-0 text-left justify-center text-dark-tertiary hidden touch-device:flex z-10 max-w-content text-xs mx-auto">
    <div className="mr-auto w-full max-w-[522px]">{children}</div>
  </div>
);

export const HeroSection = ({
  title,
  content,
  canonicalLink,
  children,
  titleBackgroundColor,
  navigateBack,
  onClose,
  shouldUseMobileTitle,
  mobileChildren,
  titleIcon,
}: HeroSectionProps) => {
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={content} />
        <link rel="canonical" href={canonicalLink} />
      </Helmet>
      <section className="h-fit w-full relative flex flex-col min-h-8">
        {!shouldUseMobileTitle && (
          <div className="flex flex-col gap-y-1 max-touch-device:p-2 max-touch-device:bg-black">
            <h1 className="text-sm text-dark-secondary touch-device:hidden">
              {title}
            </h1>
            {content && (
              <h2 className="touch-device:max-w-[520px] text-xs touch-device:hidden text-dark-tertiary">
                {content}
              </h2>
            )}
          </div>
        )}
        {isTouchDevice && shouldUseMobileTitle ? (
          <MobileHeroSectionTitle
            title={title}
            navigateBack={navigateBack}
            onClose={onClose}
            children={mobileChildren}
            titleIcon={titleIcon}
          />
        ) : (
          <DesktopHeroSectionTitle
            titleBackgroundColor={titleBackgroundColor}
            title={title}
            navigateBack={navigateBack}
            onClose={onClose}
            titleIcon={titleIcon}
          />
        )}
        {content && (
          <DesktopHeroSectionContent>{content}</DesktopHeroSectionContent>
        )}
        {children && <div className="h-full w-full">{children}</div>}
      </section>
    </>
  );
};
